import clsx from 'clsx'
import type { ReactNode } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { DocxExportNoticePhase, type DocxExportNotice } from '@/agent-v2/docx-export/schemas'
import { AlertErrorCircle, ArrowUpRight, Document, FileDownload } from '@/assets/icons'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import TruncatedText from '@/components/truncated-text/TruncatedText'

export type DocxExportNoticeCardProps = {
  notice: DocxExportNotice
  /** The exports folder in Matter Files; null hides "Open in Matter Files". */
  folderHref: string | null
  onOpenFolder?: () => void
  onDownload: () => void
  isDownloading?: boolean
  /** An export still running past the usual time says so instead of quoting it. */
  isTakingLonger?: boolean
}

const kActionClassName =
  'inline-flex items-center gap-[3px] font-medium text-brand-700 underline-offset-2 transition-colors duration-150 hover-capable:hover:text-brand-800 hover-capable:hover:underline focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 disabled:cursor-default disabled:text-brand-neutral-500 disabled:no-underline'

/**
 * An Export as .docx notice in the chat, drawn like the draft card: an icon rail, then the
 * export's state. Ready exports link to their Matter Files folder and download in place.
 */
export function DocxExportNoticeCard({
  notice,
  folderHref,
  onOpenFolder,
  onDownload,
  isDownloading = false,
  isTakingLonger = false,
}: DocxExportNoticeCardProps) {
  switch (notice.phase) {
    case DocxExportNoticePhase.STARTED:
      return (
        // The spinner never outlives the export: the ready or failed card replaces this one once the
        // export ends (withoutSettledDocxExportStarts), and an export that dies ends at its lease.
        <WithChatText text="We'll send a chat update when it's ready for download.">
          <Shell label={`.docx export of ${notice.draft_title} in progress`} rail={<CircularProgressContinuousSized size={20} thickness={4} showTrack />}>
            <Title>.docx export in progress</Title>
            {/* Wraps rather than truncates: the longer wording does not fit the docked chat panel on one line. */}
            <p className="text-left text-[12px] leading-tight text-brand-neutral-600">
              {`${isTakingLonger ? 'Taking longer than usual' : '3 - 5 min'} • Continue chatting while you wait`}
            </p>
          </Shell>
        </WithChatText>
      )

    case DocxExportNoticePhase.READY: {
      const fileName = notice.file_name ?? `${notice.draft_title}.docx`
      // The worker's note on unfinished edits can run long, so it reads as the turn's text, not inside the card.
      const isUnfinished = notice.edits_complete === false
      return (
        <WithChatText
          text={isUnfinished ? notice.delivery_note || 'Some edits may be unfinished. Review the document before you use it.' : null}
          items={isUnfinished ? notice.unfinished_items : undefined}
        >
          <Shell label={`Word document ready: ${fileName}`} tone="ready" rail={<Document className="h-6 w-6 text-brand-800" />}>
            <Title className="text-brand-800">{fileName}</Title>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] leading-tight">
              {folderHref && (
                <RouterLink to={folderHref} onClick={onOpenFolder} className={kActionClassName}>
                  Open in Matter Files
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </RouterLink>
              )}
              {notice.file_id && (
                <button
                  type="button"
                  onClick={onDownload}
                  disabled={isDownloading}
                  aria-label={isDownloading ? `Downloading ${fileName}` : `Download ${fileName}`}
                  className={kActionClassName}
                >
                  <FileDownload className="h-3.5 w-3.5" />
                  {isDownloading ? 'Downloading…' : 'Download'}
                </button>
              )}
            </div>
          </Shell>
        </WithChatText>
      )
    }

    case DocxExportNoticePhase.FAILED:
      return (
        <Shell label={`Word export failed: ${notice.draft_title}`} tone="failed" rail={<AlertErrorCircle className="h-6 w-6 text-red-700" />}>
          <Title>{`Couldn't export ${notice.draft_title}`}</Title>
          <Detail>Please try Export as .docx again.</Detail>
        </Shell>
      )
  }
}

/** The card, then any text that belongs to the turn, set like an ordinary chat answer and its lists. */
function WithChatText({ text, items, children }: { text: string | null; items?: string[]; children: ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-3">
      {children}
      {text && (
        <div className="agent-v2-response-text text-sm leading-6 whitespace-normal">
          <p>{text}</p>
          {items && items.length > 0 && (
            <ul className="ml-2 mt-1 list-disc pl-4">
              {items.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function Shell({ label, rail, tone = 'neutral', children }: { label: string; rail: ReactNode; tone?: 'neutral' | 'ready' | 'failed'; children: ReactNode }) {
  return (
    <section
      aria-label={label}
      className={clsx(
        'grid min-h-[62px] w-full max-w-[420px] grid-cols-[60px_minmax(0,1fr)] items-stretch overflow-hidden rounded-[5px] border whitespace-normal',
        tone === 'ready'
          ? 'border-brand-200 bg-brand-50'
          : tone === 'failed'
          ? 'border-red-200 bg-brand-neutral-50'
          : 'border-brand-neutral-300 bg-brand-neutral-50'
      )}
    >
      <div
        aria-hidden
        className={clsx(
          'flex h-full w-full items-center justify-center border-r',
          tone === 'ready' ? 'border-brand-200 bg-brand-100' : tone === 'failed' ? 'border-red-200 bg-red-50' : 'border-brand-neutral-300 bg-brand-neutral-100'
        )}
      >
        {rail}
      </div>
      <div className="flex min-w-0 flex-col justify-center gap-1 p-3">{children}</div>
    </section>
  )
}

function Title({ children, className }: { children: string; className?: string }) {
  return (
    <TruncatedText offsetX={-5} offsetY={-8} className={clsx('text-left text-[13px] font-medium leading-tight', className)} disableOnTouch>
      {children}
    </TruncatedText>
  )
}

function Detail({ children }: { children: string }) {
  return (
    <TruncatedText offsetX={-5} offsetY={-8} className="text-left text-[12px] leading-tight text-brand-neutral-600" disableOnTouch>
      {children}
    </TruncatedText>
  )
}
